import React from 'react';
import { Link, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import PublicHeader from '../../components/website/public/PublicHeader';
import { CustomerAuthProvider } from '../../contexts/CustomerAuthContext';
import CustomerDashboardPage from '../customer-portal/CustomerDashboardPage';
import CustomerLoginPage from '../customer-portal/CustomerLoginPage';
import BlogPage from './BlogPage';
import BlogPostPage from './BlogPostPage';
import ContactPage from './ContactPage';
import { publicCopy } from './copy';
import FinancingPage from './FinancingPage';
import HomePage from './HomePage';
import InventoryPage from './InventoryPage';
import { usePublicSitePath } from './publicSitePaths';
import { PublicSiteShellProps } from './types';
import VehicleDetailPage from './VehicleDetailPage';
import ChatBubble from '../../components/public/ChatBubble';

const PublicSiteShell: React.FC<PublicSiteShellProps> = ({
  settings,
  vehicles,
  language,
  onLanguageChange,
}) => {
  const copy = publicCopy[language];
  const phoneDisplay = settings.contact.phone || '';
  const phoneHref = phoneDisplay ? `tel:${phoneDisplay.replace(/[^+\d]/g, '')}` : '';
  const publicSitePath = usePublicSitePath();

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      <PublicHeader
        settings={settings}
        copy={copy}
        language={language}
        onLanguageChange={onLanguageChange}
      />

      <Routes>
        <Route
          index
          element={
            <HomePage
              title={copy.pages.home.title}
              subtitle={copy.pages.home.subtitle}
              ctaInventory={copy.pages.home.ctaInventory}
              ctaApply={copy.pages.home.ctaApply}
              featuredTitle={copy.pages.home.featuredTitle}
              detailCta={copy.pages.home.detailCta}
              noVehicles={copy.pages.home.noVehicles}
              vehicles={vehicles}
              language={language}
              contactPhone={settings.contact.phone}
              contactTextPhone={settings.contact.text}
              contactWhatsappPhone={settings.contact.whatsapp}
              bhphBadgeImageUrl={settings.bhphBadgeImageUrl}
              dealershipName={settings.dealershipName}
              homeExtended={copy.pages.homeExtended}
            />
          }
        />
        <Route
          path="inventory"
          element={
            <InventoryPage
              title={copy.pages.inventory.title}
              emptyState={copy.pages.inventory.emptyState}
              detailCta={copy.pages.inventory.detailCta}
              contactPhone={settings.contact.phone}
              contactTextPhone={settings.contact.text}
              contactWhatsappPhone={settings.contact.whatsapp}
              bhphBadgeImageUrl={settings.bhphBadgeImageUrl}
              labels={{
                searchPlaceholder: copy.pages.inventory.searchPlaceholder,
                make: copy.pages.inventory.make,
                bodyStyle: copy.pages.inventory.bodyStyle,
                maxPrice: copy.pages.inventory.maxPrice,
                sortBy: copy.pages.inventory.sortBy,
                sortNewest: copy.pages.inventory.sortNewest,
                sortOldest: copy.pages.inventory.sortOldest,
                sortPriceLow: copy.pages.inventory.sortPriceLow,
                sortPriceHigh: copy.pages.inventory.sortPriceHigh,
                reset: copy.pages.inventory.reset,
                allMakes: copy.pages.inventory.allMakes,
                allBodyStyles: copy.pages.inventory.allBodyStyles,
              }}
              vehicles={vehicles}
            />
          }
        />
        <Route
          path="inventory/:vehicleId"
          element={
            <VehicleDetailPage
              vehicles={vehicles}
              labels={copy.pages.vehicleDetail}
              contactPhone={settings.contact.phone}
              bhphBadgeImageUrl={settings.bhphBadgeImageUrl}
            />
          }
        />
        <Route path="blog" element={<BlogPage language={language} />} />
        <Route path="blog/:slug" element={<BlogPostPage language={language} />} />
        <Route
          path="financing"
          element={
            <FinancingPage
              title={copy.pages.financing.title}
              body={copy.pages.financing.body}
              primaryCta={copy.pages.financing.primaryCta}
              secondaryCta={copy.pages.financing.secondaryCta}
              startApplication={copy.pages.financing.startApplication}
              wizard={copy.pages.financing.wizard}
              fullName={copy.pages.financing.fullName}
              phone={copy.pages.financing.phone}
              email={copy.pages.financing.email}
              message={copy.pages.financing.message}
              submit={copy.pages.financing.submit}
              success={copy.pages.financing.success}
              submitError={copy.pages.financing.submitError}
              language={language}
            />
          }
        />
        <Route
          path="contact"
          element={
            <ContactPage
              title={copy.pages.contact.title}
              labels={{
                fullName: copy.pages.contact.fullName,
                phone: copy.pages.contact.phone,
                email: copy.pages.contact.email,
                message: copy.pages.contact.message,
                submit: copy.pages.contact.submit,
                success: copy.pages.contact.success,
                submitError: copy.pages.contact.submitError,
              }}
              contact={settings.contact}
            />
          }
        />
        <Route
          element={
            <CustomerAuthProvider>
              <Outlet />
            </CustomerAuthProvider>
          }
        >
          <Route path="customer/login" element={<CustomerLoginPage />} />
          <Route path="customer/dashboard" element={<CustomerDashboardPage />} />
        </Route>
        <Route path="*" element={<Navigate to={publicSitePath('/')} replace />} />
      </Routes>

      <ChatBubble language={language} />

      {/* Mobile sticky bottom bar */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 md:hidden"
        style={{ fontFamily: "'Oswald', sans-serif" }}
      >
        <div className="grid grid-cols-3">
          {phoneDisplay ? (
            <a
              href={phoneHref}
              className="flex flex-col items-center justify-center bg-red-600 hover:bg-red-700 text-white py-3 px-2 transition-colors"
            >
              <span className="text-xl leading-none">📞</span>
              <span className="font-bold text-sm mt-0.5 tracking-wide">{phoneDisplay}</span>
              <span className="text-red-200 text-xs">
                {language === 'es' ? 'Llamar' : 'Tap to Call'}
              </span>
            </a>
          ) : (
            <div className="flex flex-col items-center justify-center bg-red-600 text-white py-3 px-2">
              <span className="text-xl leading-none">📞</span>
              <span className="font-bold text-sm mt-0.5 tracking-wide">
                {language === 'es' ? 'Llamar' : 'Call'}
              </span>
            </div>
          )}
          <Link
            to={publicSitePath('/customer/login')}
            className="flex flex-col items-center justify-center text-white py-3 px-2 transition-colors"
            style={{ backgroundColor: '#0A1628' }}
          >
            <span className="text-xl leading-none">👤</span>
            <span className="font-bold text-sm mt-0.5 tracking-wide">
              {copy.navigation.myAccount}
            </span>
            <span className="text-gray-400 text-xs">{language === 'es' ? 'Portal' : 'Portal'}</span>
          </Link>
          <Link
            to={publicSitePath('/financing')}
            className="flex flex-col items-center justify-center text-white py-3 px-2 transition-colors"
            style={{ backgroundColor: '#1D4ED8' }}
          >
            <span className="text-xl leading-none">📋</span>
            <span className="font-bold text-sm mt-0.5 tracking-wide">
              {language === 'es' ? 'Aplicar' : 'Apply Online'}
            </span>
            <span className="text-blue-200 text-xs">
              {language === 'es' ? 'Toma 2 Min' : 'Takes 2 Min'}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicSiteShell;
